/**
 * Admin listino API client — F-009.4.
 *
 *  - GET   /api/v1/admin/listino           → { items: ListinoItemView[] }
 *  - PATCH /api/v1/admin/listino           → ListinoItemView (riga aggiornata)
 *
 * Mirror di `features/admin-wallet/lib/admin-client.ts`: `credentials: 'include'`,
 * errori → `ListinoApiError` con status+code, `errorMessageFor(code)` per i toast.
 * I prezzi BigInt sono serializzati dall'API come stringhe decimali (cents).
 */
import type { PraticaTipologiaValue } from '@app/shared';

const API = '/api/v1/admin/listino';

export interface ListinoItemView {
  tipologia: PraticaTipologiaValue;
  /** Prezzo agenti/commercialisti (cents come stringa). */
  prezzoCents: string;
  /** Prezzo privati — pay-per-practice (cents come stringa, F-010). */
  prezzoPrivatiCents: string;
  updatedAt: string | null;
}

/** Prezzi da aggiornare per una tipologia (euro); almeno uno richiesto. */
export interface UpdateTariffaPrices {
  prezzoEuro?: number;
  prezzoPrivatiEuro?: number;
}

export interface ListinoListResponse {
  items: ListinoItemView[];
}

export class ListinoApiError extends Error {
  readonly status: number;
  readonly code: string;
  constructor(status: number, code: string, message: string) {
    super(message);
    this.name = 'ListinoApiError';
    this.status = status;
    this.code = code;
  }
}

export function isListinoApiError(err: unknown): err is ListinoApiError {
  return err instanceof ListinoApiError;
}

const ERROR_MESSAGES: Record<string, string> = {
  VALIDATION_ERROR: 'Prezzo non valido. Controlla il valore inserito.',
  LISTINO_VALIDATION: 'Prezzo non valido.',
  FORBIDDEN: 'Accesso negato.',
  UNAUTHORIZED: "Sessione scaduta. Effettua di nuovo l'accesso.",
};

export function errorMessageFor(err: unknown): string {
  if (isListinoApiError(err)) {
    return ERROR_MESSAGES[err.code] ?? err.message ?? 'Si è verificato un errore.';
  }
  if (err instanceof TypeError) return 'Errore di connessione. Riprova.';
  return 'Si è verificato un errore. Riprova.';
}

interface ApiErrorEnvelope {
  code?: string;
  message?: string;
}

async function throwForError(res: Response, fallbackCode: string, fallbackMsg: string): Promise<never> {
  const env = (await res
    .json()
    .catch(() => ({ code: 'UNKNOWN', message: 'Errore sconosciuto' }))) as ApiErrorEnvelope;
  throw new ListinoApiError(res.status, env.code ?? fallbackCode, env.message ?? fallbackMsg);
}

export async function getListino(): Promise<ListinoListResponse> {
  const res = await fetch(API, { credentials: 'include' });
  if (!res.ok) await throwForError(res, 'LIST_FAILED', 'Impossibile caricare il listino');
  return (await res.json()) as ListinoListResponse;
}

export async function updateTariffa(
  tipologia: PraticaTipologiaValue,
  prices: UpdateTariffaPrices,
): Promise<ListinoItemView> {
  const res = await fetch(API, {
    method: 'PATCH',
    credentials: 'include',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ tipologia, ...prices }),
  });
  if (!res.ok) await throwForError(res, 'UPDATE_FAILED', 'Impossibile aggiornare il prezzo');
  return (await res.json()) as ListinoItemView;
}
